import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

function DOLDetermination() {
  const [formData, setFormData] = useState({
    dolEligible: 'Yes',
    dolReviewScore: '',
    dolComments: '',
  });
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const API_BASE_URL = process.env.REACT_APP_API_URL || '';
  const activeStep = 5;
  const steps = [
    { number: 1, title: 'Upload Document', description: 'Attach your application file' },
    { number: 2, title: 'Applicant Information', description: 'Enter contact and address details' },
    { number: 3, title: 'Project Information', description: 'Describe the project and funding needs' },
    { number: 4, title: 'Budget', description: 'Provide budget allocations' },
    { number: 5, title: 'DOL Determination', description: 'Complete final eligibility review' },
  ];

  useEffect(() => {
    const storedData = sessionStorage.getItem('newApplicationData');
    if (storedData) {
      try {
        const parsed = JSON.parse(storedData);
        if (!parsed.program) {
          alert('Please select a program from the Dashboard before creating an application.');
          navigate('/dashboard');
          return;
        }
        if (parsed.dol) {
          setFormData(parsed.dol);
        }
      } catch {
        sessionStorage.removeItem('newApplicationData');
      }
    } else {
      alert('Please select a program from the Dashboard before creating an application.');
      navigate('/dashboard');
    }
  }, [navigate]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError('');
    setSuccessMessage('');

    const currentData = JSON.parse(sessionStorage.getItem('newApplicationData') || '{}');
    const payloadData = {
      ...currentData.userInfo,
      ...currentData.projectInfo,
      ...currentData.budget,
      grantProgram: currentData.program?.name || currentData.program?.programCode || '',
      programCode: currentData.program?.programCode || currentData.program?.id || '',
      dolEligible: formData.dolEligible,
      dolReviewScore: formData.dolReviewScore,
      dolComments: formData.dolComments,
      documentName: currentData.document?.name,
      documentType: currentData.document?.type,
    };

    console.log('DOL submit payload:', payloadData);

    try {
      setIsSubmitting(true);
      const apiEndpoint = `${API_BASE_URL}/api/grants`;
      console.log('DOL submit apiEndpoint:', apiEndpoint);
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payloadData),
      });
      console.log('DOL submit response status:', response.status, response.statusText);

      const responseText = await response.text();
      let result;
      try {
        result = responseText ? JSON.parse(responseText) : null;
      } catch (parseError) {
        console.error('Failed to parse JSON response:', parseError, 'body:', responseText);
        result = null;
      }

      console.log('DOL submit response body:', result || responseText);

      if (!response.ok) {
        const fallbackBody = responseText || 'No response body';
        throw new Error(result?.message || `Submission failed with status ${response.status}: ${fallbackBody}`);
      }
      sessionStorage.removeItem('newApplicationData');
      setSuccessMessage('Application submitted successfully. Redirecting to dashboard...');
      setTimeout(() => navigate('/dashboard'), 1200);
    } catch (error) {
      console.error('DOL submit error:', error);
      setSubmitError(error.message || 'Unable to submit application.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-stack">
        <section className="app-flow-header">
          <div>
            <p className="app-flow-tag">Application Flow</p>
            <h1>DOL Determination</h1>
            <p className="app-flow-description">
              Provide eligibility determination status and reviewer input for final approval.
            </p>
          </div>
          <div className="step-badges">
            {steps.map((step) => (
              <div key={step.number} className={`step-item ${activeStep === step.number ? 'active' : ''}`}>
                <div className="step-number">{step.number}</div>
                <div className="step-text">
                  <div className="step-title">{step.title}</div>
                  <div className="step-desc">{step.description}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="application-content">
          <div className="form-card">
            <h2 className="section-heading">Step 5: DOL Determination</h2>
            <p className="form-description">
              Finalize the application with eligibility status, review score, and reviewer comments.
            </p>
            <form onSubmit={handleSubmit} noValidate>
              <div className="form-row">
                <label className="form-field">
                  <span>Eligibility Determination</span>
                  <select name="dolEligible" value={formData.dolEligible} onChange={handleChange}>
                    <option value="Yes">Eligible</option>
                    <option value="No">Not eligible</option>
                  </select>
                </label>
                <label className="form-field">
                  <span>Review Score</span>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    name="dolReviewScore"
                    value={formData.dolReviewScore}
                    onChange={handleChange}
                    onInvalid={(e) => e.preventDefault()}
                    className={submitError && !formData.dolReviewScore ? 'input-error' : ''}
                    placeholder="0-100"
                  />
                </label>
              </div>
              <label className="form-field">
                <span>Reviewer Comments</span>
                <textarea
                  name="dolComments"
                  value={formData.dolComments}
                  onChange={handleChange}
                  placeholder="Add comments or determination notes"
                  rows={5}
                />
              </label>
              {submitError && <div className="section-error">{submitError}</div>}
              {successMessage && <div className="success-message">{successMessage}</div>}
              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={() => navigate('/dashboard/new-application/budget')}>
                  Back to Budget
                </button>
                <button type="submit" className="submit-btn" disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : 'Submit Final Application'}
                </button>
              </div>
            </form>
          </div>
        </section>
    </div>
  );
}

export default DOLDetermination;
